import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Building2, ChevronRight } from "lucide-react";
import { adminApi } from "../../lib/api";

const SPONSOR_TYPES = [
  ["church", "Church"],
  ["christian_retailer", "Christian Retailer"],
  ["bookstore", "Bookstore / Gift Store"],
  ["ministry", "Ministry"],
  ["school", "Christian School"],
  ["nonprofit", "Nonprofit"],
  ["publisher", "Publisher / Media"],
  ["conference", "Conference / Event"],
  ["corporate_partner", "Corporate Partner"],
  ["other", "Other"],
];

export default function AdminSponsorsListPage() {
  const [sponsors, setSponsors] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    sponsor_name: "",
    sponsor_type: "church",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    start_date: "",
    notes: "",
    audience_size: "",
    primary_color: "#4F6F52",
    status: "draft",
  });

  const reload = () =>
    adminApi
      .listSponsors()
      .then(setSponsors)
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));

  useEffect(() => {
    reload();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.audience_size === "") delete payload.audience_size;
      else payload.audience_size = parseInt(payload.audience_size, 10);
      // Drop empty optional strings so the backend stores null instead of "".
      ["contact_name", "contact_email", "contact_phone", "website", "start_date", "notes"].forEach(
        (k) => {
          if (payload[k] === "" || payload[k] == null) delete payload[k];
        }
      );
      await adminApi.createSponsor(payload);
      toast.success("Sponsor created.");
      setShowForm(false);
      setForm({
        sponsor_name: "",
        sponsor_type: "church",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        website: "",
        start_date: "",
        notes: "",
        audience_size: "",
        primary_color: "#4F6F52",
        status: "draft",
      });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create sponsor.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-sponsors-list" className="space-y-8 fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={16} className="text-[#4F6F52]" />
            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#4F6F52]">
              Sponsors
            </span>
          </div>
          <h1 className="font-display text-4xl">Sponsor Organizations</h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/sponsors-analytics"
            data-testid="admin-sponsors-analytics-link"
            className="text-sm text-[#4F6F52] hover:text-[#3F5A42] underline underline-offset-2"
          >
            View sponsor analytics
          </Link>
          <button
            data-testid="admin-new-sponsor-btn"
            onClick={() => setShowForm((v) => !v)}
            className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-4 py-2 text-sm inline-flex items-center gap-1.5"
          >
            <Plus size={14} /> New sponsor
          </button>
        </div>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          data-testid="admin-sponsor-form"
          className="rounded-3xl border border-[#DCE5DB] bg-white p-6 space-y-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Sponsor name" required>
              <input
                data-testid="sponsor-name"
                required
                value={form.sponsor_name}
                onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5 focus:outline-none focus:border-[#4F6F52]"
              />
            </Field>
            <Field label="Type">
              <select
                data-testid="sponsor-type"
                value={form.sponsor_type}
                onChange={(e) => setForm({ ...form, sponsor_type: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
              >
                {SPONSOR_TYPES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Contact name">
              <input
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
              />
            </Field>
            <Field label="Contact email">
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
              />
            </Field>
            <Field label="Contact phone">
              <input
                data-testid="sponsor-contact-phone"
                type="tel"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
                placeholder="(555) 123-4567"
              />
            </Field>
            <Field label="Website">
              <input
                data-testid="sponsor-website"
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
                placeholder="https://example.org"
              />
            </Field>
            <Field label="Start date">
              <input
                data-testid="sponsor-start-date"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
              />
            </Field>
            <Field label="Audience size">
              <input
                type="number"
                min={0}
                value={form.audience_size}
                onChange={(e) => setForm({ ...form, audience_size: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
              />
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
          </div>
          <Field label="Notes">
            <textarea
              data-testid="sponsor-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5 resize-none"
              placeholder="Internal notes — never shown publicly."
            />
          </Field>
          <button
            data-testid="sponsor-submit"
            type="submit"
            disabled={saving}
            className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-2.5 text-sm disabled:opacity-60"
          >
            {saving ? "Saving…" : "Create sponsor"}
          </button>
        </form>
      )}

      <div className="space-y-3">
        {sponsors === null && (
          <div className="text-[#6B6B6B]">Loading…</div>
        )}
        {sponsors && sponsors.length === 0 && (
          <div
            data-testid="admin-sponsors-empty"
            className="rounded-2xl border border-dashed border-[#DCE5DB] p-8 text-center text-[#6B6B6B]"
          >
            No sponsors yet. Click <strong>New sponsor</strong> to add one.
          </div>
        )}
        {sponsors &&
          sponsors.map((s) => (
            <Link
              key={s.sponsor_id}
              to={`/admin/sponsors/${s.sponsor_id}/manage`}
              data-testid={`sponsor-row-${s.sponsor_slug}`}
              className="flex items-center justify-between rounded-2xl border border-[#DCE5DB] bg-white hover:bg-[#EEF3EC]/60 px-5 py-4 transition"
            >
              <div>
                <div className="font-medium">{s.sponsor_name}</div>
                <div className="text-xs text-[#6B6B6B] mt-0.5">
                  {s.sponsor_type} · /sponsor/{s.sponsor_slug} · ref{" "}
                  <code className="text-[#4F6F52]">{s.referral_code}</code>
                </div>
              </div>
              <span
                className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${
                  s.status === "active"
                    ? "bg-[#E1ECDD] text-[#4F6F52]"
                    : "bg-[#F0EDE7] text-[#6B6B6B]"
                }`}
              >
                {s.status}
              </span>
              <ChevronRight size={14} className="text-[#6B6B6B] ml-2" />
            </Link>
          ))}
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs text-[#6B6B6B] mb-1.5">
        {label}
        {required && <span className="text-[#7C3E33]"> *</span>}
      </div>
      {children}
    </label>
  );
}
